import moment from 'moment';
import { FC, useEffect, useRef, useState } from 'react';
import { DayPicker, Locale, Modifiers } from 'react-day-picker';
import { enUS, zhCN } from 'react-day-picker/locale';
import { Fade } from 'components/Transition';
import UI from 'stores/ui';
import classlist from 'utils/classlist';
import { language } from 'utils/i18n';
import YearMonth from './YearMonth';
import 'moment/locale/zh-cn';
import 'react-day-picker/style.css';
import './style.scss';

export interface DatePickerProps {
    value?: Date | moment.Moment;
    onChange?(value: moment.Moment): void;
    format?: string;
    disabled?: boolean;
    onFocus?(): void;
    onBlur?(): void;
    maxDate?: Date | moment.Moment;
    minDate?: Date | moment.Moment;
    placeholder?: string;
    locale?: Locale;
    initialDate?: Date | moment.Moment; // 初始默认显示的时间
    isInvalid?: boolean;
}

/**
 * @description
 * 日期选择组件，该组件用于表单，需要配合FormGroup使用。
 * 默认情况下为最近100年范围。可以通过minDate、maxDate来修改可选日期范围
 */
const DatePicker: FC<DatePickerProps> = ({
    disabled,
    isInvalid,
    value = null,
    format = 'YYYY-MM-DD',
    maxDate = moment().add(50, 'year'),
    minDate = moment().subtract(50, 'year'),
    initialDate = moment(),
    locale = language === 'en_US' ? enUS : zhCN,
    placeholder = __('请选择日期'),
    onChange,
    onBlur,
    onFocus
}) => {
    const momentValue = moment(value || initialDate);
    const [visible, setVisible] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date>(momentValue.toDate());
    const inputRef = useRef<HTMLInputElement>(null);
    const dayPickerRef = useRef<HTMLDivElement>(null);

    const handleDayClick = (date: Date, modifiers: Modifiers) => {
        if (!modifiers.disabled) {
            onChange!(moment(date));

            setVisible(false);
        }
    };

    // 禁止选择超过最大 最小日期范围的时间
    const disabledDays = (date: Date) => {
        const now = moment(date);

        // now的时间是12点, 直接比较会导致minDate的时间过了12点之后, isBefore比较的结果不符合预期, 所以此处只比较day
        return now.isBefore(minDate, 'day') || now.isAfter(maxDate, 'day');
    };

    useEffect(() => {
        const handleClick = (ev: MouseEvent) => {
            if (!disabled && !dayPickerRef.current?.contains(ev.target as HTMLElement)) {
                if (ev.target === inputRef.current) {
                    setVisible(visible => !visible);
                } else {
                    setVisible(false);
                }
            }
        };

        window.addEventListener('click', handleClick);

        return () => {
            window.removeEventListener('click', handleClick);
        };
    }, [disabled]);

    return (
        <div
            className={classlist('date-picker-root', {
                'date-picker-active': visible,
                'firefox-disabled': UI.isFireFox && disabled
            })}>
            <input
                className={classlist('form-control', { 'is-invalid': isInvalid })}
                value={value ? momentValue.format(format) : ''}
                // firefox上input的disable状态不能添加onClick事件, 所以在火狐上disabled使用Readonly代替
                readOnly
                disabled={!UI.isFireFox && disabled}
                onFocus={onFocus}
                onBlur={onBlur}
                placeholder={placeholder}
                ref={inputRef}
            />
            <Fade in={visible}>
                <div ref={dayPickerRef} className="container">
                    <DayPicker
                        mode="single"
                        captionLayout="dropdown"
                        locale={locale}
                        onDayClick={handleDayClick}
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        showOutsideDays
                        disabled={disabledDays}
                        defaultMonth={momentValue.toDate()}
                        startMonth={moment(minDate).toDate()}
                        endMonth={moment(maxDate).toDate()}
                        components={{
                            MonthCaption: ({ calendarMonth }) => (
                                <YearMonth
                                    date={calendarMonth.date}
                                    localeUtil={moment}
                                    locale={locale}
                                    maxDate={moment(maxDate).toDate()}
                                    minDate={moment(minDate).toDate()}
                                />
                            )
                        }}
                        required
                    />
                </div>
            </Fade>
        </div>
    );
};

export default DatePicker;
